import CSREntry from 'src/clientRoutes/_app.tags.artwork.$tag.csr'
import FullSSREntry from 'src/clientRoutes/_app.tags.artwork.$tag.full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { type ListArtworksQueryVariables } from 'src/services/apollo/generated/graphql'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import {
  buildTagItemListJsonLd,
  PROD_DOMAIN,
  tagMeta,
  tagMetaLoader,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.tags.artwork.$tag'

/**
 * Minimal shape of an artwork preview edge node used by the tag ItemList
 * JSON-LD builder below. Matches the fields selected by
 * `fetchListTagArtworksForSSR` and the Node-fetch / Python fallback in
 * `fetchArtworkTagPreviewConnectionWithDebug`.
 */
type ArtworkPreviewNode = {
  id: string
  title?: string | null
  media?: {
    thumbnailUrl?: string | null
    urls?: Array<{ variant?: string | null; url?: string | null } | null> | null
  } | null
}

const getClientModule = () => import('src/clientRoutes/_app.tags.artwork.$tag')

export const handle = {
  name: 'tags/artwork',
  track: { scene: 'tags-artwork' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

async function fetchArtworkTagPreviewConnectionWithDebug(
  variables: Pick<
    ListArtworksQueryVariables,
    'first' | 'orderBy' | 'isSafeSearch' | 'tag' | 'tackId' | 'q'
  >,
) {
  try {
    const { fetchListTagArtworksForSSR } =
      await import('src/utils/ssr-meta/graphql/artwork.server')
    const connection = await fetchListTagArtworksForSSR(variables)
    if (connection) {
      return {
        connection,
        error: null as string | null,
      }
    }

    const query = `
      query TagArtworkPreviewForSSR(
        $first: Int
        $orderBy: String
        $isSafeSearch: Boolean
        $tag: String
        $tackId: String
        $q: String
      ) {
        artworks(
          first: $first
          orderBy: $orderBy
          isSafeSearch: $isSafeSearch
          tag: $tag
          tackId: $tackId
          q: $q
        ) {
          edges {
            cursor
            node {
              __typename
              id
              title
              prompts
              createdAt
              mediaId
              type
              authorId
              likedCount
              liked
              commentCount
              videoMediaId
              media {
                __typename
                id
                width
                height
                thumbnailUrl
                urls {
                  variant
                  url
                }
                flag {
                  shouldBlur
                }
              }
              author {
                __typename
                id
                displayName
                username
                avatarMediaId
                avatarSmallThumbnailMediaUrl
              }
              flag {
                shouldBlur
              }
              manga {
                __typename
                id
                coverMediaId
                chapter {
                  __typename
                  coverMediaId
                }
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
            startCursor
          }
          feedUuid
        }
      }
    `

    const response = await fetch('https://api.pixai.art/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        variables,
      }),
    })

    if (!response.ok) {
      return {
        connection: null,
        error: `fallback-http-${response.status}`,
      }
    }

    const json = (await response.json()) as {
      data?: {
        artworks?: ReturnType<typeof Object> | null
      }
      errors?: Array<{ message: string }>
    }

    return {
      connection: (json.data?.artworks ?? null) as any,
      error: json.errors?.[0]?.message ?? null,
    }
  } catch (error) {
    return {
      connection: null,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

export async function loader({ params, request, context }: Route.LoaderArgs) {
  const [
    { fetchListTagArtworksForSSR },
    { fetchTagForSSR, fetchTackByCodeNameForSSR },
  ] = await Promise.all([
    import('src/utils/ssr-meta/graphql/artwork.server'),
    import('src/utils/ssr-meta/graphql/tag.server'),
  ])
  const { t } = getI18n(context)

  const [tag, tack] = await Promise.all([
    params.tag ? fetchTagForSSR(params.tag) : Promise.resolve(null),
    params.tag ? fetchTackByCodeNameForSSR(params.tag) : Promise.resolve(null),
  ])
  const displayName = tag?.displayName || tag?.name || params.tag
  // Mirror the CSR TagPage decision here: when `useGetTackByCodeName`
  // resolves a tack we query `ListArtworks` by `tackId`, and only fall
  // back to the raw slug as `tag` when no tack exists. Previously the
  // loader was always forcing `{ tag: params.tag, tackId: undefined }`,
  // which works for slugs that happen to equal the tag's display name
  // (e.g. "pokemon" / "umamusume") but returns 0 results for slugs that
  // diverge from the backend tag name (e.g. "genshin_impact" vs
  // "Genshin Impact").
  //
  // The full key set must also mirror `BaseArtworkInfiniteScroll`'s
  // `parsedListArgs` (including the keys whose values end up as
  // `undefined`), because our Apollo `relayStylePagination` is
  // configured with a dynamic `keyArgs = Object.keys(args).filter(...)`
  // — every key, even those bound to `undefined`, changes the cache
  // field identifier. If the SSR seed writes under
  // `{tackId, first, isSafeSearch, orderBy}` while the CSR query looks
  // up `{isSafeSearch, tag, tackId, q, relevantArtworkId, authorId,
  // type, i2vPro, first, feed, orderBy}`, the two end up in different
  // cache slots and cache-first will miss during hydrate → UI flashes
  // into "No result" while the real query is in flight.
  const url = new URL(request.url)
  const searchQ = url.searchParams.get('q') ?? undefined
  const listArtworkVariablesForSSR: ListArtworksQueryVariables = {
    isSafeSearch: true,
    tag: tack?.id ? undefined : params.tag,
    tackId: tack?.id ?? undefined,
    q: searchQ,
    relevantArtworkId: undefined,
    authorId: undefined,
    type: undefined,
    i2vPro: undefined,
    first: 24,
    feed: undefined,
    orderBy: '-markInfo.likedCount',
  }
  const previewResult = params.tag
    ? await fetchArtworkTagPreviewConnectionWithDebug(
        listArtworkVariablesForSSR,
      )
    : { connection: null, error: null as string | null }
  const previewConnection = previewResult.connection

  // Build a schema.org `ItemList` JSON-LD describing the first-screen
  // artworks for this tag. Emitted from route `meta()` in `<head>` so
  // Google can surface the list as a curated collection in search results
  // without waiting for hydration.
  const preliminaryMeta = tagMetaLoader({
    type: 'artwork',
    tagSlug: params.tag,
    tag,
    requestUrl: request.url,
  })
  const artworkItemListJsonLd =
    previewConnection && displayName
      ? buildTagItemListJsonLd({
          type: 'artwork',
          displayName,
          tagDescription: preliminaryMeta.description,
          canonicalUrl: preliminaryMeta.canonicalUrl,
          entries: (
            (previewConnection.edges ?? []) as Array<
              { node?: ArtworkPreviewNode | null } | null | undefined
            >
          )
            .map(edge => edge?.node)
            .filter(
              (node): node is ArtworkPreviewNode =>
                !!node && typeof node.id === 'string',
            )
            .map(node => {
              const title = node.title?.trim() || undefined
              const media = node.media
              const thumb =
                media?.thumbnailUrl ??
                media?.urls?.find(u => u?.variant === 'STILL_THUMBNAIL')?.url ??
                media?.urls?.find(u => u?.variant === 'PUBLIC')?.url ??
                undefined
              return {
                id: node.id,
                url: `https://${PROD_DOMAIN}/artwork/${node.id}`,
                name: title,
                image: thumb ?? undefined,
              }
            }),
        })
      : undefined

  const meta = tagMetaLoader({
    type: 'artwork',
    tagSlug: params.tag,
    tag,
    requestUrl: request.url,
    jsonLd: artworkItemListJsonLd,
  })

  // `serverGraphqlFetch` sends the raw query document without Apollo's
  // automatic `__typename` injection, so the tack / connection objects
  // come back without `__typename` fields. Apollo's InMemoryCache uses
  // `__typename + id` to normalize entities; without `__typename` the
  // `cache.writeQuery` call in the route's default Component either
  // fails to normalize at all or writes fields that read back as
  // `null`. The visible symptom was `useGetTackByCodeName` returning
  // `{ tack: { id: null, codeName: null, ... } }` after hydrate, which
  // in turn left `tagPageState.tackId` as `undefined` and caused
  // `<StatefulArtworkGrid>` to issue an un-filtered-by-tackId
  // `ListArtworks({ tag: "genshin_impact" })` query — which returns 0
  // results for slugs whose display name differs from the slug.
  //
  // Patch the missing `__typename` fields here on the server so that
  // the seed data round-trips through Apollo normalization cleanly.
  const tackWithTypename = tack
    ? {
        ...tack,
        __typename: 'Tack' as const,
      }
    : null
  const previewConnectionWithTypename = previewConnection
    ? {
        __typename: 'ArtworkConnection' as const,
        ...previewConnection,
        edges: (previewConnection.edges ?? []).map((edge: unknown): unknown => {
          if (!edge || typeof edge !== 'object') return edge
          const e = edge as { node?: unknown; cursor?: string }
          return {
            __typename: 'ArtworkEdge' as const,
            ...e,
            node:
              e.node && typeof e.node === 'object'
                ? { __typename: 'Artwork' as const, ...(e.node as object) }
                : e.node,
          }
        }),
        pageInfo: previewConnection.pageInfo
          ? {
              __typename: 'PageInfo' as const,
              ...previewConnection.pageInfo,
            }
          : previewConnection.pageInfo,
      }
    : null

  return {
    ...meta,
    tag,
    artworkTagApollo: {
      tagSlug: params.tag ?? '',
      tackData: tackWithTypename ? { tack: tackWithTypename } : undefined,
      // Seed the same variables we actually fetched with so that the
      // client-side `useInfiniteListArtworks` cache-first hit lands on
      // this cache entry instead of issuing a fresh query.
      listArtworkVariables: listArtworkVariablesForSSR,
      listArtworkData: previewConnectionWithTypename
        ? { artworks: previewConnectionWithTypename }
        : undefined,
    },
    initialArtworkConnectionData: previewConnectionWithTypename ?? undefined,
    tagShell:
      tag && displayName
        ? {
            type: 'artwork' as const,
            displayName,
            title: meta.title ?? '',
            description: meta.description ?? '',
            image: meta.image,
            canonicalUrl: meta.canonicalUrl,
            heroTitle: t('common:tags.artwork.hero.title', {
              tag: displayName,
            }),
            heroDescription: t('common:tags.artwork.hero.description', {
              tag: displayName,
            }),
          }
        : undefined,
  }
}

export function meta({ loaderData }: Route.MetaArgs) {
  return tagMeta(loaderData)
}

/**
 * Dual-mode entry. `'off'` (CSR) path renders `TagPage` directly
 * under `<RootRuntime>`'s global Apollo / QueryClient with no SSR
 * seed; `'on'` (Full SSR) path mounts the route-scoped Apollo
 * bridge defined in `clientRoutes/_app.tags.artwork.$tag.full-ssr.tsx`.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
